import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './StudentHome.css';
import TimeIn from '../../TimeIn/TimeIn';
import { FaHome, FaChartLine, FaUser, FaClock, FaComments, FaCalendarCheck } from 'react-icons/fa';
import { FaArrowRightFromBracket, FaLocationDot } from 'react-icons/fa6';
import { useStudentAuth } from '../../../lib/AuthProvider';
import { getSupabase } from '../../../lib/supabaseClient';

const StudentHome: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { student, loading: authLoading, signOut } = useStudentAuth();
  const [showTimeIn, setShowTimeIn] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path;
  };
  
  // Check session validity on component mount and periodically
  useEffect(() => {
    const checkSession = async () => {
      try {
        const supabase = getSupabase();
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error || !session) {
          console.log('Session invalid in StudentHome, redirecting to login');
          navigate('/', { replace: true });
          return;
        }
        
        // If session exists but no student data and not loading, wait a bit more
        if (!student && !authLoading) {
          console.log('No student data but session exists, waiting for AuthProvider to load...');
          // Give AuthProvider more time to load the profile
          setTimeout(() => {
            if (!student && !authLoading) {
              console.log('Still no student data after timeout, redirecting to login');
              navigate('/', { replace: true });
            }
          }, 2000);
        }
      } catch (error) {
        console.error('Error checking session in StudentHome:', error);
      }
    };

    // Only check session if we're not loading
    if (!authLoading) {
      checkSession();
    }
    
    // Check session periodically
    const intervalId = setInterval(checkSession, 60000); // Check every minute
    
    return () => clearInterval(intervalId);
  }, [student, authLoading, navigate]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="student-home">
      <header className="top-nav">
        <div className="logo">
          <h1>InternTr<FaLocationDot className="location-icon" />ckr</h1>
        </div>
        <button
          className="hamburger-btn"
          aria-label="Open menu"
          aria-controls="mobile-sidebar"
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen(true)}
        >
          {/* Unicode hamburger icon */}
          {'\u2630'}
        </button>
        <div className="nav-links">
          <button 
            onClick={() => navigate('/student/home')} 
            className={`nav-link ${isActive('/student/home') ? 'active' : ''}`}
          >
            <FaHome className="nav-icon" /> Home
          </button>
          <button 
            onClick={() => navigate('/student/attendance')} 
            className={`nav-link ${isActive('/student/attendance') ? 'active' : ''}`}
          >
            <FaClock className="nav-icon" /> Attendance
          </button>
          <button 
            onClick={() => navigate('/student/progress')} 
            className={`nav-link ${isActive('/student/progress') ? 'active' : ''}`}
          >
            <FaChartLine className="nav-icon" /> Progress
          </button>
          <button 
            onClick={() => navigate('/student/feedback')} 
            className={`nav-link ${isActive('/student/feedback') ? 'active' : ''}`}
          >
            <FaComments className="nav-icon" /> Feedback
          </button>
          <button 
            onClick={() => navigate('/student/profile')} 
            className={`nav-link ${isActive('/student/profile') ? 'active' : ''}`}
          >
            <FaUser className="nav-icon" /> Profile
          </button>
          <button onClick={handleSignOut} className="nav-link sign-out-btn">
            <FaArrowRightFromBracket className="nav-icon" /> Sign Out
          </button>
        </div>
      </header>

      {/* Mobile sidebar and backdrop */}
      <div
        className={`backdrop${isMenuOpen ? ' open' : ''}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden={!isMenuOpen}
      />
      <aside
        id="mobile-sidebar"
        className={`sidebar${isMenuOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="sidebar-header">
          <h2>Menu</h2>
          <button
            className="close-btn"
            aria-label="Close menu"
            onClick={() => setIsMenuOpen(false)}
          >
            {'\u00D7'}
          </button>
        </div>
        <nav className="sidebar-links" onClick={() => setIsMenuOpen(false)}>
          <button 
            onClick={() => navigate('/student/home')} 
            className="nav-link"
          >
            <FaHome className="nav-icon" /> Home
          </button>
          <button 
            onClick={() => navigate('/student/attendance')} 
            className="nav-link"
          >
            <FaClock className="nav-icon" /> Attendance
          </button>
          <button 
            onClick={() => navigate('/student/progress')} 
            className="nav-link"
          >
            <FaChartLine className="nav-icon" /> Progress
          </button>
          <button 
            onClick={() => navigate('/student/feedback')} 
            className="nav-link"
          >
            <FaComments className="nav-icon" /> Feedback
          </button>
          <button 
            onClick={() => navigate('/student/profile')} 
            className="nav-link"
          >
            <FaUser className="nav-icon" /> Profile
          </button>
          <button onClick={handleSignOut} className="nav-link sign-out-btn">
            <FaArrowRightFromBracket className="nav-icon" /> Sign Out
          </button>
        </nav>
      </aside>

      <main className="main-content">
        <div className="welcome-section">
          <h1>Welcome,<br/>
            {authLoading ? "Loading..." : 
              student?.last_name && student?.first_name ? 
                `${student.last_name}, ${student.first_name}` :
                "Student"
            }
          </h1>
          <p>How are we feeling today?</p>
        </div>

        <div className="dashboard-grid centered">
          {/* Quick Actions Panel */}
          <div className="panel actions-panel">
            <div className="panel-header">Quick Actions</div>
            <div className="panel-content">
              <button className="action-button primary" onClick={() => setShowTimeIn(true)}>
                <FaClock className="btn-icon" /> Time In / Time Out
              </button>
              <button className="action-button" onClick={() => navigate('/student/attendance')}>
                <FaCalendarCheck className="btn-icon" /> View Attendance
              </button>
              <button className="action-button" onClick={() => navigate('/student/feedback')}>
                <FaComments className="btn-icon" /> Submit Feedback
              </button>
            </div>
          </div>
        </div>
      </main>
      {showTimeIn && <TimeIn onClose={() => setShowTimeIn(false)} />}
    </div>
  );
};

export default StudentHome;
